import { useEffect, useState } from 'react';
import { compileScriptFunction } from '@utils/scriptEval';

/**
 * Скомпилированный пользовательский скрипт onRowPrepared/onCellPrepared.
 * Первый аргумент — событие грида (row/cell), второй — ключ грида (если есть).
 */
export type UserScriptHandler = (eventArg: unknown, gridKey?: string) => void;

/**
 * Компилирует текст пользовательского скрипта в функцию с явными параметрами.
 * Замена прямому eval на месте вызова: скрипт видит только `row`/`cell` и
 * `gridKey`, а не весь локальный скоуп компонента, и парсится один раз,
 * а не на каждую строку/ячейку.
 */
const compileUserScript = (eventArgName: 'row' | 'cell', script: string): UserScriptHandler | undefined => {
    try {
        return compileScriptFunction([eventArgName, 'gridKey'], script) as UserScriptHandler;
    } catch (error) {
        console.log(`Ошибка компиляции пользовательского скрипта (${eventArgName}). Подробности ниже.`);
        console.error(error);
        return undefined;
    }
};

export const useUserScripts = (onRowPreparedUrl?: string, onCellPreparedUrl?: string) => {
    const [onRowPreparedScript, setOnRowPreparedScript] = useState<UserScriptHandler>();
    const [onCellPreparedScript, setOnCellPreparedScript] = useState<UserScriptHandler>();

    useEffect(() => {
        // Handle onRowPrepared script
        if (onRowPreparedUrl) {
            fetch(onRowPreparedUrl).then((response) => {
                console.log(`onRowPrepared. Загружен скрипт: ${onRowPreparedUrl}`);
                const contentType = response.headers.get('Content-Type');
                if (contentType === 'text/javascript' || contentType === 'application/javascript') {
                    response.text().then((script) => {
                        setOnRowPreparedScript(() => compileUserScript('row', script));
                    });
                } else {
                    console.log(
                        `onRowPrepared. Файл ${onRowPreparedUrl} не является JS файлом (ContentType: ${contentType}). Скрипт не будет исполнен.`,
                    );
                }
            });
        } else {
            setOnRowPreparedScript(undefined);
        }

        // Handle onCellPrepared script
        if (onCellPreparedUrl) {
            fetch(onCellPreparedUrl).then((response) => {
                console.log(`onCellPrepared. Загружен скрипт: ${onCellPreparedUrl}`);
                const contentType = response.headers.get('Content-Type');
                if (contentType === 'text/javascript' || contentType === 'application/javascript') {
                    response.text().then((script) => {
                        setOnCellPreparedScript(() => compileUserScript('cell', script));
                    });
                } else {
                    console.log(
                        `onCellPrepared. Файл ${onCellPreparedUrl} не является JS файлом (ContentType: ${contentType}). Скрипт не будет исполнен.`,
                    );
                }
            });
        } else {
            setOnCellPreparedScript(undefined);
        }
    }, [onRowPreparedUrl, onCellPreparedUrl]);

    return { onRowPreparedScript, onCellPreparedScript };
};
